/**
 * Phase 4A — preview blob URL ownership and cleanup.
 *
 * Model:
 * - Each blob: URL created for a preview is registered and retained by one or more artwork ids.
 * - previewUrl and displayUrl typically share the same string; owners are per-art, not per-field.
 * - Revoke runs only when the last owner releases the URL (idempotent).
 * - Non-blob URLs (data:, http:) are ignored.
 * - IndexedDB must not store blob URL strings (Phase 4E removes that path).
 * - Session previewCache Map may hold blob URLs; eviction helpers clean Map entries only.
 */

export type PreviewBlobDiagnostics = {
  activeUrls: number;
  ownerEntries: number;
  revokeCount: number;
  doubleRevokeSkips: number;
  unknownReleaseSkips: number;
};

const ownersByUrl = new Map<string, Set<string>>();
const createdUrls = new Set<string>();
const revokedUrls = new Set<string>();

let revokeCount = 0;
let doubleRevokeSkips = 0;
let unknownReleaseSkips = 0;

function isDev(): boolean {
  return typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';
}

function isBlobUrl(url: string | null | undefined): url is string {
  return typeof url === 'string' && url.startsWith('blob:');
}

function uniqueBlobUrls(...urls: Array<string | null | undefined>): string[] {
  const set = new Set<string>();
  for (const u of urls) {
    if (isBlobUrl(u)) set.add(u);
  }
  return Array.from(set);
}

function logDiag(message: string, detail?: unknown): void {
  if (!isDev()) return;
  if (detail !== undefined) {
    // eslint-disable-next-line no-console
    console.debug(`[previewBlobLifecycle] ${message}`, detail);
  } else {
    // eslint-disable-next-line no-console
    console.debug(`[previewBlobLifecycle] ${message}`);
  }
}

/** Create an object URL for a preview PNG and retain it for the artwork owner. */
export function createPreviewBlobUrl(blob: Blob, ownerId: string): string {
  const url = URL.createObjectURL(blob);
  createdUrls.add(url);
  retainPreviewBlobUrl(url, ownerId);
  return url;
}

/** Add an artwork owner for an existing blob URL (e.g. shared previewCache hit). */
export function retainPreviewBlobUrl(url: string | null | undefined, ownerId: string): void {
  if (!isBlobUrl(url) || !ownerId) return;
  let owners = ownersByUrl.get(url);
  if (!owners) {
    owners = new Set();
    ownersByUrl.set(url, owners);
  }
  owners.add(ownerId);
}

/**
 * Release one artwork's claim on a blob URL.
 * When the last owner releases, revoke exactly once.
 */
export function releasePreviewBlobUrl(url: string | null | undefined, ownerId: string): void {
  if (!isBlobUrl(url) || !ownerId) return;

  const owners = ownersByUrl.get(url);
  if (!owners || !owners.has(ownerId)) {
    unknownReleaseSkips += 1;
    logDiag('release of unknown or already-released owner skipped', { url, ownerId });
    return;
  }

  owners.delete(ownerId);
  if (owners.size > 0) return;

  ownersByUrl.delete(url);
  revokePreviewBlobUrlOnce(url);
}

/** Release previewUrl/displayUrl for one artwork (same URL revoked at most once). */
export function releaseArtPreviewUrls(art: {
  id: string;
  previewUrl?: string | null;
  displayUrl?: string | null;
}): void {
  if (!art || !art.id) return;
  for (const url of uniqueBlobUrls(art.previewUrl, art.displayUrl)) {
    releasePreviewBlobUrl(url, art.id);
  }
}

/** Release many artworks (e.g. multi-delete / clear queue). */
export function releaseArtsPreviewUrls(
  arts: Array<{ id: string; previewUrl?: string | null; displayUrl?: string | null }>
): void {
  for (const art of arts) {
    releaseArtPreviewUrls(art);
  }
}

/**
 * Force-release a URL that was created for an artwork which no longer exists
 * (Worker finished after delete). Idempotent.
 */
export function abandonPreviewBlobUrl(url: string | null | undefined, ownerId: string): void {
  if (!isBlobUrl(url)) return;
  const owners = ownersByUrl.get(url);
  if (owners && owners.has(ownerId)) {
    releasePreviewBlobUrl(url, ownerId);
    return;
  }
  // Created/retained then art vanished before retain completed — revoke if we created it.
  if (createdUrls.has(url) && !revokedUrls.has(url)) {
    ownersByUrl.delete(url);
    revokePreviewBlobUrlOnce(url);
  }
}

function revokePreviewBlobUrlOnce(url: string): void {
  if (revokedUrls.has(url)) {
    doubleRevokeSkips += 1;
    logDiag('double revoke skipped', url);
    return;
  }
  try {
    URL.revokeObjectURL(url);
  } catch (e) {
    logDiag('revokeObjectURL threw', e);
  }
  revokedUrls.add(url);
  createdUrls.delete(url);
  revokeCount += 1;
  logDiag('revoked', url);
}

/** Drop in-memory previewCache entries that point at a given blob URL. Does not touch durable storage. */
export function evictPreviewCacheEntriesForUrls(
  cache: Map<string, string>,
  urls: Iterable<string | null | undefined>
): void {
  const want = new Set(uniqueBlobUrls(...Array.from(urls)));
  if (want.size === 0) return;
  for (const [key, value] of Array.from(cache.entries())) {
    if (want.has(value)) {
      cache.delete(key);
    }
  }
}

/**
 * Clear the in-memory previewCache Map.
 * Does not revoke URLs still owned by artworks.
 * Revokes any registered URLs that somehow have zero owners (orphan safety).
 */
export function clearPreviewCacheMap(cache: Map<string, string>): void {
  cache.clear();
  revokeOrphanedPreviewBlobs();
}

/** Revoke blob URLs we created that currently have no owners. */
export function revokeOrphanedPreviewBlobs(): void {
  for (const url of Array.from(createdUrls)) {
    const owners = ownersByUrl.get(url);
    if (!owners || owners.size === 0) {
      ownersByUrl.delete(url);
      revokePreviewBlobUrlOnce(url);
    }
  }
}

export function getPreviewBlobOwnerCount(url: string): number {
  return ownersByUrl.get(url)?.size ?? 0;
}

export function getPreviewBlobDiagnostics(): PreviewBlobDiagnostics {
  let ownerEntries = 0;
  for (const set of ownersByUrl.values()) ownerEntries += set.size;
  return {
    activeUrls: ownersByUrl.size,
    ownerEntries,
    revokeCount,
    doubleRevokeSkips,
    unknownReleaseSkips,
  };
}

/** Test-only reset of module state. */
export function __resetPreviewBlobLifecycleForTests(): void {
  for (const url of Array.from(createdUrls)) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
  ownersByUrl.clear();
  createdUrls.clear();
  revokedUrls.clear();
  revokeCount = 0;
  doubleRevokeSkips = 0;
  unknownReleaseSkips = 0;
}
